import {
  Dialog,
  Flex,
  Button,
  Heading,
  Text,
  TextField,
  FormField,
  TextArea,
  IconButton,
  Icon,
  Separator,
  Callout,
  Spinner,
  Link,
  Tooltip,
  Checkbox,
} from '@scale/scaleui-radix';
import { faClose, faPlay, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { UserAutocomplete } from 'corp/views/CorpProjectGroupManager/apps/Webinars/components/UserAutocomplete';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import {
  useCreateCohort,
  useUpdateCohort,
  useGetMembershipEval,
  useFetchAllWorkerSkills,
  useCheckCohortNameAvailability,
  useGetContributorDetails,
  useGetCohortMappingsByConsumer,
} from '../../../../communication/apiClient';
import { FilterGroup, isValidFilterGroup } from '@scale/compass-shared/filter';
import { CohortDTO, CohortSubscriptionType } from '@scale/cohorts';
import {
  INPUT_COLUMNS,
  SCOPING_COLUMNS,
} from 'react-scale/corp/views/DataCompassCardsV2/adapter/ContributorsAdapterCard/constants';
import { Filters, type ColumnComponentProps } from '@scale/compass-ui/components/filters/Filters';
import { COLUMN_ID_TO_OPTIONS } from 'react-scale/corp/views/DataCompassCardsV2/components/filters/inputFilterOptions';
import { useWorkerSourceOptions } from 'corp/hooks/useWorkerSource';
import Papa from 'papaparse';
import save from 'save-file';
import { EvaluationResultsSection } from './EvaluationResultsSection';
import { CohortOption, CohortSelector } from './CohortSelector';
import { Column } from '@scale/compass-shared/types';

const CloseConfirmationPanel = ({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  return (
    <Flex direction="column" gap="4">
      <Heading size="4">Confirm Exit</Heading>
      <Text size="3" color="neutral">
        Are you sure you want to exit? Any unsaved changes will be discarded.
      </Text>
      <Flex gap="3" justify="end">
        <Button variant="soft" color="neutral" onClick={onCancel}>
          Cancel
        </Button>
        <Dialog.Close>
          <Button onClick={onConfirm}>Exit</Button>
        </Dialog.Close>
      </Flex>
    </Flex>
  );
};

const EvaluateButton = ({
  onClick,
  evaluateButtonDisabled,
  isFetchingSimilarCohorts,
}: {
  onClick: () => void;
  evaluateButtonDisabled: boolean;
  isFetchingSimilarCohorts: boolean;
}) => {
  return (
    <Tooltip content="Find similar cohorts and how many contributors match the current filters">
      <Button
        size="2"
        variant="outline"
        onClick={onClick}
        disabled={evaluateButtonDisabled}
        color={evaluateButtonDisabled ? 'neutral' : 'accent'}
      >
        <Spinner loading={isFetchingSimilarCohorts} size="2">
          <Icon icon={faPlay} label="Evaluate Potential Cohort Members" size="2" />
        </Spinner>
        <Text size="2" color={evaluateButtonDisabled ? 'neutral' : 'accent'}>
          Evaluate
        </Text>
      </Button>
    </Tooltip>
  );
};

const UpsertCohortButton = ({
  canUpsertCohort,
  isUpsertingCohort,
  onUpsertCohort,
  isEditMode,
}: {
  canUpsertCohort: boolean;
  isUpsertingCohort: boolean;
  onUpsertCohort: () => void;
  isEditMode?: boolean;
}) => {
  const buttonText = isEditMode
    ? isUpsertingCohort
      ? 'Updating...'
      : 'Update'
    : isUpsertingCohort
    ? 'Creating...'
    : 'Create';

  const tooltipContent = isEditMode
    ? 'To update a cohort: ensure all filters are valid, provide a description, and evaluate if filters have changed'
    : 'To create a cohort: ensure all filters are valid, provide a unique name, add a description, and evaluate the cohort';

  return (
    <>
      <Tooltip content={tooltipContent}>
        <Button
          size="3"
          variant="solid"
          disabled={isUpsertingCohort || !canUpsertCohort}
          onClick={onUpsertCohort}
        >
          {buttonText}
        </Button>
      </Tooltip>
    </>
  );
};

const CohortFormFields = ({
  cohortNameState,
  cohortDescriptionState,
  cohortOwnerIdsState,
  ignoreAutoArchivalState,
  nameIsAvailable,
  isCheckingNameAvailability,
  canEditCohortName,
  canEditCohortDescription,
}: {
  cohortNameState: [string, (name: string) => void];
  cohortDescriptionState: [string, (description: string) => void];
  cohortOwnerIdsState: [string[], (ownerIds: string[]) => void];
  ignoreAutoArchivalState: [boolean, (ignoreAutoArchival: boolean) => void];
  nameIsAvailable: boolean;
  isCheckingNameAvailability: boolean;
  canEditCohortName?: boolean;
  canEditCohortDescription?: boolean;
}) => {
  const [newCohortName, setNewCohortName] = cohortNameState;
  const [newCohortDescription, setNewCohortDescription] = cohortDescriptionState;
  const [newCohortOwnerIds, setNewCohortOwnerIds] = cohortOwnerIdsState;
  const [ignoreAutoArchival, setIgnoreAutoArchival] = ignoreAutoArchivalState;

  return (
    <>
      <FormField.Root>
        <FormField.Label size="2">Name this cohort</FormField.Label>
        <TextField.Root
          id="my-form-id"
          placeholder="Name this cohort"
          value={newCohortName}
          onChange={e => setNewCohortName(e.target.value)}
          color={!nameIsAvailable && !isCheckingNameAvailability ? 'error' : undefined}
          disabled={!canEditCohortName}
          className={!canEditCohortName ? 'cursor-not-allowed opacity-60' : ''}
        >
          {isCheckingNameAvailability && (
            <TextField.Slot side="right">
              <Spinner size="1" />
            </TextField.Slot>
          )}
        </TextField.Root>
        {!canEditCohortName ? (
          <Text size="1" color="neutral">
            Cohort name cannot be edited.
          </Text>
        ) : (
          newCohortName.trim().length > 0 &&
          !isCheckingNameAvailability && (
            <Text size="1" color={nameIsAvailable ? 'success' : 'error'}>
              {nameIsAvailable
                ? 'This name is available.'
                : 'A cohort with this name already exists. Please choose a different name.'}
            </Text>
          )
        )}
      </FormField.Root>
      <FormField.Root>
        <Text as="label" size="2">
          <Flex gap="2">
            <Checkbox
              size="1"
              checked={ignoreAutoArchival}
              onCheckedChange={setIgnoreAutoArchival}
            />
            Analytics cohort
          </Flex>
        </Text>
      </FormField.Root>
      <FormField.Root>
        <FormField.Label size="2">Describe this cohort</FormField.Label>
        <TextArea
          id="my-form-id"
          placeholder="Describe this cohort"
          autoResize
          value={newCohortDescription}
          onChange={e => setNewCohortDescription(e.target.value)}
          disabled={!canEditCohortDescription}
          className={!canEditCohortDescription ? 'cursor-not-allowed opacity-60' : ''}
        />
        {!canEditCohortDescription ? (
          <Text size="1" color="neutral">
            Cohort description cannot be edited.
          </Text>
        ) : null}
      </FormField.Root>
      <Flex direction="column" gap="3" width="100%">
        <Heading size="4">Cohort owner</Heading>
        <UserAutocomplete
          userIds={newCohortOwnerIds}
          onUserIdsChange={setNewCohortOwnerIds}
          limitToScaliens
          limitToContributors={false}
          showValues={true}
        />
        <Text className="text-neutral-11" size="1">
          Only cohort owners will be allowed to edit cohort information. The current user will
          automatically be added as an owner.
        </Text>
      </Flex>
    </>
  );
};

const CohortFiltersSection = ({
  filters,
  setFilters,
  workerSkillOptions,
  workerSourceOptions,
  excludeFraudHoldingState,
  isFetchingSimilarCohorts,
  refetchSimilarCohorts,
  evaluateButtonDisabled,
  setCohortIsEvaluated,
  cohortToEdit,
  initialCohort,
  setCohortMappings,
  showScopingError,
  inputColumns,
  scopingColumns,
}: {
  filters: FilterGroup;
  setFilters: (filters: FilterGroup) => void;
  workerSkillOptions: { value: string; label: string }[];
  workerSourceOptions: { value: string; label: string }[];
  excludeFraudHoldingState: [boolean, (excludeFraudHolding: boolean) => void];
  isFetchingSimilarCohorts: boolean;
  refetchSimilarCohorts: () => void;
  evaluateButtonDisabled: boolean;
  setCohortIsEvaluated: (evaluated: boolean) => void;
  cohortToEdit?: CohortDTO;
  initialCohort?: CohortDTO;
  setCohortMappings: Dispatch<SetStateAction<Map<string, CohortSubscriptionType>>>;
  showScopingError: boolean;
  inputColumns?: Column[];
  scopingColumns?: Column[];
}) => {
  const [excludeFraudHolding, setExcludeFraudHolding] = excludeFraudHoldingState;

  const consumerId = cohortToEdit?._id.toString() ?? initialCohort?._id.toString();

  const CohortFilterComponent = useMemo(
    () => (props: ColumnComponentProps) =>
      (
        <CohortSelector
          onMappingsChange={setCohortMappings}
          defaultCohortVersionId={props.value}
          consumerId={consumerId}
          consumerType="COHORT"
          onValueChange={(option: CohortOption | undefined) =>
            props.onValueChange(option?.cohortVersionId ?? '')
          }
          disabled={props.disabled}
        />
      ),
    [setCohortMappings, consumerId],
  );

  return (
    <>
      <Flex direction="column" gap="3" width="100%">
        <div className="scaleui scale-theme scale-tailwind">
          <Filters
            filters={filters}
            setFilters={setFilters}
            columns={inputColumns ?? Object.values(INPUT_COLUMNS)}
            scopingColumns={scopingColumns ?? Object.values(SCOPING_COLUMNS)}
            scopingColumnDefaultFilterGroups={{
              [SCOPING_COLUMNS.workerSkill.id]: {
                operator: 'and',
                filtersOrFilterGroups: [
                  {
                    column: SCOPING_COLUMNS.workerSkill,
                    condition: 'is',
                    term: '',
                  },
                  {
                    column: INPUT_COLUMNS.hasWorkerSkill,
                    condition: 'true',
                    term: '',
                  },
                ],
              },
            }}
            columnOptionMap={{
              ...COLUMN_ID_TO_OPTIONS,
              workerSkill: workerSkillOptions,
              workerSource: workerSourceOptions,
            }}
            columnComponentMap={{
              cohortIds: CohortFilterComponent,
            }}
          />
        </div>
        {showScopingError && (
          <Callout.Root size="1" color="error">
            <Callout.Icon>
              <Icon icon={faTriangleExclamation} label="Error" size="2" />
            </Callout.Icon>
            <Callout.Text>
              Scoping filters (Project ID, Worker Skill Name) must be grouped with at least one
              other filter. Please add another filter to the same group or remove the scoping
              filter.
            </Callout.Text>
          </Callout.Root>
        )}
        <Flex direction="row" gap="2" justify="between" align="center">
          <Tooltip content="Filter out contributors who are in fraud holding.">
            <Text className="cursor-pointer" as="label" size="2">
              <Flex gap="2">
                <Checkbox
                  size="1"
                  checked={excludeFraudHolding}
                  onCheckedChange={setExcludeFraudHolding}
                />{' '}
                Exclude contributors in fraud holding
              </Flex>
            </Text>
          </Tooltip>
          <EvaluateButton
            onClick={() => {
              refetchSimilarCohorts();
              setCohortIsEvaluated(true);
            }}
            evaluateButtonDisabled={evaluateButtonDisabled}
            isFetchingSimilarCohorts={isFetchingSimilarCohorts}
          />
        </Flex>
        <Text className="text-neutral-11" size="1">
          {
            <Link
              href="https://scale.atlassian.net/wiki/spaces/OP/pages/1083899908/Contributor+Roster+Search"
              underline="always"
              target="_blank"
            >
              Criteria are the same as CB Search.
            </Link>
          }
        </Text>
      </Flex>
    </>
  );
};

const useHandleUpsertCohort = ({
  isEditMode,
  cohortToEdit,
  newCohortName,
  filters,
  newCohortDescription,
  newCohortOwnerIds,
  excludeFraudHolding,
  ignoreAutoArchival,
  cohortMappings,
  clonedFromCohortId,
  resetCohortInfo,
  onCohortCreated,
  setIsUpsertingCohort,
  workforceProjectId,
}: {
  isEditMode?: boolean;
  cohortToEdit?: CohortDTO;
  newCohortName: string;
  filters: FilterGroup;
  newCohortDescription: string;
  newCohortOwnerIds: string[];
  excludeFraudHolding: boolean;
  ignoreAutoArchival: boolean;
  cohortMappings: Map<string, CohortSubscriptionType>;
  clonedFromCohortId: string | undefined;
  resetCohortInfo: () => void;
  onCohortCreated: (createdCohort?: CohortDTO) => void;
  setIsUpsertingCohort: (isUpserting: boolean) => void;
  workforceProjectId?: string;
}) => {
  const { mutate: createCohort } = useCreateCohort();
  const { mutate: updateCohort } = useUpdateCohort();

  return useCallback(() => {
    setIsUpsertingCohort(true);
    const cohortMappingsToSend: Record<string, string> = Object.fromEntries(
      cohortMappings.entries(),
    );

    if (isEditMode && cohortToEdit) {
      // Update existing cohort
      updateCohort(
        {
          cohortId: cohortToEdit._id,
          currentCohortVersionId: cohortToEdit.latestVersionId?.toString() ?? '',
          name: newCohortName,
          filters,
          description: newCohortDescription,
          ownerIds: newCohortOwnerIds,
          excludeCBsInFraudHolding: excludeFraudHolding,
          ignoreAutoArchival,
          cohortMappings: cohortMappingsToSend,
        },
        {
          onSuccess: () => {
            resetCohortInfo();
            setIsUpsertingCohort(false);
            onCohortCreated();
          },
          onError: () => {
            setIsUpsertingCohort(false);
          },
        },
      );
    } else {
      // Create new cohort
      createCohort(
        {
          name: newCohortName,
          filters,
          description: newCohortDescription,
          ownerIds: newCohortOwnerIds,
          clonedFromCohort: clonedFromCohortId,
          excludeCBsInFraudHolding: excludeFraudHolding,
          ignoreAutoArchival,
          cohortMappings: cohortMappingsToSend,
          workforceProjectId,
        },
        {
          onSuccess: data => {
            resetCohortInfo();
            setIsUpsertingCohort(false);
            onCohortCreated(data);
          },
          onError: () => {
            setIsUpsertingCohort(false);
          },
        },
      );
    }
  }, [
    isEditMode,
    cohortToEdit,
    newCohortName,
    filters,
    newCohortDescription,
    newCohortOwnerIds,
    excludeFraudHolding,
    ignoreAutoArchival,
    cohortMappings,
    clonedFromCohortId,
    resetCohortInfo,
    onCohortCreated,
    setIsUpsertingCohort,
    createCohort,
    updateCohort,
    workforceProjectId,
  ]);
};

const UpsertCohortPanel = ({
  potentialContributorCount,
  isFetchingSimilarCohorts,
  refetchSimilarCohorts,
  exactMatchCohort,
  onExistingCohortClicked,
  onCohortCreated,
  filters,
  setFilters,
  filtersAreValid,
  hasNonPairedScopedFields,
  cohortNameState,
  cohortDescriptionState,
  cohortOwnerIdsState,
  excludeCBsInFraudHoldingState,
  ignoreAutoArchivalState,
  onClose,
  clonedFromCohortId,
  resetCohortInfo,
  isEditMode,
  cohortToEdit,
  hasMetadataChanges,
  filtersOrFraudHoldingChanged,
  inflowCount,
  outflowCount,
  cohortMappings,
  setCohortMappings,
  initialCohort,
  workforceProjectId,
  inputColumns,
  scopingColumns,
}: {
  potentialContributorCount: number;
  isFetchingSimilarCohorts: boolean;
  refetchSimilarCohorts: () => void;
  exactMatchCohort: CohortDTO | undefined;
  onExistingCohortClicked: (cohortId: string) => void;
  onCohortCreated: (createdCohort?: CohortDTO) => void;
  filters: FilterGroup;
  setFilters: (filters: FilterGroup) => void;
  filtersAreValid: boolean;
  hasNonPairedScopedFields: boolean;
  cohortNameState: [string, (name: string) => void];
  cohortDescriptionState: [string, (description: string) => void];
  cohortOwnerIdsState: [string[], (ownerIds: string[]) => void];
  excludeCBsInFraudHoldingState: [boolean, (excludeCBsInFraudHolding: boolean) => void];
  ignoreAutoArchivalState: [boolean, (ignoreAutoArchival: boolean) => void];
  onClose: () => void;
  clonedFromCohortId: string | undefined;
  resetCohortInfo: () => void;
  isEditMode?: boolean;
  cohortToEdit?: CohortDTO;
  initialCohort?: CohortDTO;
  hasMetadataChanges: boolean;
  filtersOrFraudHoldingChanged: boolean;
  inflowCount?: number;
  outflowCount?: number;
  cohortMappings: Map<string, CohortSubscriptionType>;
  setCohortMappings: Dispatch<SetStateAction<Map<string, CohortSubscriptionType>>>;
  workforceProjectId?: string;
  inputColumns?: Column[];
  scopingColumns?: Column[];
}) => {
  const [isUpsertingCohort, setIsUpsertingCohort] = useState(false);

  const newCohortName = cohortNameState[0];
  const newCohortDescription = cohortDescriptionState[0];
  const newCohortOwnerIds = cohortOwnerIdsState[0];
  const excludeFraudHolding = excludeCBsInFraudHoldingState[0];
  const ignoreAutoArchival = ignoreAutoArchivalState[0];

  // Check if cohort name is available (only needed in create mode)
  const { data: nameAvailabilityData, isLoading: isCheckingNameAvailability } =
    useCheckCohortNameAvailability(useDebouncedValue(newCohortName, 500));
  const nameIsAvailable = isEditMode ? true : nameAvailabilityData?.available;

  // cohort is evaluated should be set to false whenever filters, cohort name, descriotion, or owner ids change.
  // This means the cohort must be evaluated before it can be created, allowing us to check for similar cohorts
  // or exact match cohorts.
  const [cohortIsEvaluated, setCohortIsEvaluated] = useState(false);
  useEffect(() => {
    setCohortIsEvaluated(false);
  }, [filters, newCohortName, newCohortDescription, newCohortOwnerIds, excludeFraudHolding]);

  const requiredFieldsPopulated =
    newCohortName && newCohortDescription && newCohortOwnerIds.length > 0;
  const canEvaluateCohort: boolean = !!(
    requiredFieldsPopulated &&
    filtersAreValid &&
    !isFetchingSimilarCohorts &&
    !isCheckingNameAvailability &&
    nameIsAvailable
  );

  const MAX_COHORT_SIZE = 1_000_000;
  const exceedsMaxCohortSize = potentialContributorCount >= MAX_COHORT_SIZE;

  // In edit mode, allow update if:
  // - Has changes AND
  // - If filters/fraud holding changed, must evaluate first
  // - If only description/owners changed, no evaluation needed
  // Block creation/update if cohort size exceeds max limit
  const canUpsertCohort: boolean = useMemo(() => {
    if (exceedsMaxCohortSize) {
      return false;
    }
    if (isEditMode) {
      if (!hasMetadataChanges && !filtersOrFraudHoldingChanged) {
        return false;
      }
      if (filtersOrFraudHoldingChanged) {
        return cohortIsEvaluated && canEvaluateCohort;
      }
      return true;
    }
    return canEvaluateCohort && cohortIsEvaluated;
  }, [
    exceedsMaxCohortSize,
    isEditMode,
    hasMetadataChanges,
    filtersOrFraudHoldingChanged,
    cohortIsEvaluated,
    canEvaluateCohort,
  ]);

  const { data: workerSkills } = useFetchAllWorkerSkills(false, 0);
  const workerSkillOptions = useMemo(
    () =>
      workerSkills?.map(skill => ({
        value: skill.id,
        label: skill.display_name ?? skill.name,
      })) ?? [],
    [workerSkills],
  );

  const workerSources = useWorkerSourceOptions({ filterOutCrowd: false });
  const workerSourceOptions = useMemo(() => {
    const seen = new Set<string>();
    return workerSources.reduce<{ value: string; label: string }[]>((acc, option) => {
      if (!seen.has(option.source)) {
        seen.add(option.source);
        acc.push({ value: option.source, label: option.name });
      }
      return acc;
    }, []);
  }, [workerSources]);

  const handleUpsertCohort = useHandleUpsertCohort({
    isEditMode,
    cohortToEdit,
    newCohortName,
    filters,
    newCohortDescription,
    newCohortOwnerIds,
    excludeFraudHolding,
    ignoreAutoArchival,
    cohortMappings,
    clonedFromCohortId,
    resetCohortInfo,
    onCohortCreated,
    setIsUpsertingCohort,
    workforceProjectId,
  });

  const { isFetching: isContributorDetailsLoading, refetch: fetchContributorDetails } =
    useGetContributorDetails(filters, excludeFraudHolding, cohortToEdit?._id);

  const downloadCSV = useCallback(async () => {
    const result = await fetchContributorDetails();
    if (result.data?.contributors) {
      const name = newCohortName + '-proposed-contributors-' + new Date().toISOString();
      const csv = Papa.unparse(result.data.contributors, { header: true });
      await save(csv, name + '.csv');
    }
  }, [newCohortName, fetchContributorDetails, filters, excludeFraudHolding]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Flex justify="between" mb="3">
        <Dialog.Title size="6">{isEditMode ? 'Edit Cohort' : 'Create new Cohort'}</Dialog.Title>
        <Flex direction="row" gap="2" align="center">
          <Dialog.Close>
            <IconButton color="neutral" variant="ghost" onClick={onClose}>
              <Icon icon={faClose} label="Close" size="2" />
            </IconButton>
          </Dialog.Close>
        </Flex>
      </Flex>
      <Flex direction="row" gap="4">
        <Flex direction="column" gap="4" width="100%">
          <CohortFormFields
            cohortNameState={cohortNameState}
            cohortDescriptionState={cohortDescriptionState}
            cohortOwnerIdsState={cohortOwnerIdsState}
            ignoreAutoArchivalState={ignoreAutoArchivalState}
            nameIsAvailable={nameIsAvailable || false}
            isCheckingNameAvailability={isCheckingNameAvailability}
            canEditCohortName={!isEditMode && workforceProjectId === undefined}
            canEditCohortDescription={workforceProjectId === undefined}
          />
          <Separator orientation="horizontal" size="4" />
          <CohortFiltersSection
            filters={filters}
            setFilters={setFilters}
            workerSkillOptions={workerSkillOptions}
            workerSourceOptions={workerSourceOptions}
            excludeFraudHoldingState={excludeCBsInFraudHoldingState}
            isFetchingSimilarCohorts={isFetchingSimilarCohorts}
            refetchSimilarCohorts={refetchSimilarCohorts}
            evaluateButtonDisabled={!canEvaluateCohort}
            setCohortIsEvaluated={setCohortIsEvaluated}
            cohortToEdit={cohortToEdit}
            initialCohort={initialCohort}
            setCohortMappings={setCohortMappings}
            showScopingError={
              hasNonPairedScopedFields &&
              isValidFilterGroup(filters) &&
              filters.filtersOrFilterGroups.length > 0
            }
            inputColumns={inputColumns}
            scopingColumns={scopingColumns}
          />
          {cohortIsEvaluated && (
            <EvaluationResultsSection
              potentialContributorCount={potentialContributorCount}
              exactMatchCohort={exactMatchCohort}
              onExistingCohortClicked={onExistingCohortClicked}
              onDownloadCSVClicked={downloadCSV}
              isDownloadingCSV={isContributorDetailsLoading}
              inflowCount={inflowCount}
              outflowCount={outflowCount}
              isEditMode={isEditMode}
            />
          )}
          <Flex justify="end" gap="2" align="center">
            <UpsertCohortButton
              canUpsertCohort={canUpsertCohort}
              isUpsertingCohort={isUpsertingCohort}
              onUpsertCohort={handleUpsertCohort}
              isEditMode={isEditMode}
            />
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export const UpsertCohortDialog = ({
  initialCohort,
  cohortToEdit,
  onCohortCreated,
  onExistingCohortClicked,
  currentUserId,
  onConfirmedClose,
  workforceProjectId,
  defaultFilters,
  defaultCohortValueMappings,
  defaultTitle,
  defaultDescription,
  inputColumns,
  scopingColumns,
}: {
  initialCohort: CohortDTO | undefined;
  cohortToEdit?: CohortDTO;
  onCohortCreated: (createdCohort?: CohortDTO) => void;
  onExistingCohortClicked: (cohortId: string) => void;
  currentUserId: string;
  onConfirmedClose: () => void;
  workforceProjectId?: string;
  defaultFilters?: FilterGroup;
  defaultCohortValueMappings?: Map<string, CohortOption>;
  defaultTitle?: string;
  defaultDescription?: string;
  inputColumns?: Column[];
  scopingColumns?: Column[];
}) => {
  const isEditMode = !!cohortToEdit;

  // When editing or cloning a cohort, ignore defaultFilters entirely
  const hasCohortSource = !!(cohortToEdit || initialCohort);

  const internalDefaultFilter: FilterGroup = useMemo(
    () => ({
      operator: 'and' as const,
      filtersOrFilterGroups: [],
    }),
    [],
  );

  // If we have a cohort source, use its filters; otherwise use defaultFilters prop
  const effectiveDefaultFilters = hasCohortSource
    ? cohortToEdit?.filters || initialCohort?.filters || internalDefaultFilter
    : defaultFilters ?? internalDefaultFilter;

  const [filters, setFilters] = useState<FilterGroup>(effectiveDefaultFilters);
  const userHasModifiedFilters = useRef(false);

  // Update filters when defaultFilters changes, but only if:
  // - User hasn't made changes
  // - We're NOT in edit/clone mode (cohort source takes precedence)
  useEffect(() => {
    if (!userHasModifiedFilters.current && !hasCohortSource) {
      setFilters(effectiveDefaultFilters);
    }
  }, [effectiveDefaultFilters, hasCohortSource]);

  const handleSetFilters = useCallback(
    (newFilters: FilterGroup) => {
      // Guard: If we have a cohort source and we haven't modified the filters, ignore any attempts to set the original defaultFilters
      // This prevents the Filters component from overwriting cohort filters during initialization
      if (hasCohortSource && defaultFilters && !userHasModifiedFilters.current) {
        const newMatchesDefault = JSON.stringify(newFilters) === JSON.stringify(defaultFilters);
        if (newMatchesDefault) {
          return;
        }
      }

      userHasModifiedFilters.current = true;
      setFilters(newFilters);
    },
    [hasCohortSource, defaultFilters],
  );

  const [cohortName, setCohortName] = useState(defaultTitle ?? '');
  const [cohortDescription, setCohortDescription] = useState(defaultDescription ?? '');
  const [cohortOwnerIds, setCohortOwnerIds] = useState<string[]>([currentUserId]);
  const [excludeCBsInFraudHolding, setExcludeCBsInFraudHolding] = useState(true);
  const [ignoreAutoArchival, setIgnoreAutoArchival] = useState(workforceProjectId !== undefined);
  const [cohortCreationCloseConfirmationOpen, setCohortCreationCloseConfirmationOpen] =
    useState(false);

  // Cohort mappings state (for cohorts that reference other cohorts)
  const [cohortMappings, setCohortMappings] = useState<Map<string, CohortSubscriptionType>>(
    new Map<string, CohortSubscriptionType>(),
  );

  const { data: cohortMappingsData } = useGetCohortMappingsByConsumer(
    cohortToEdit?._id ?? initialCohort?._id ?? '',
    'COHORT',
    { enabled: !!(cohortToEdit?._id ?? initialCohort?._id) },
  );

  // Initialize cohortMappings from defaultCohortValueMappings or cohortMappingsData
  useEffect(() => {
    const mappings = new Map<string, CohortSubscriptionType>();

    // Use defaultCohortValueMappings if provided
    if (defaultCohortValueMappings && defaultCohortValueMappings.size > 0) {
      defaultCohortValueMappings.forEach((value, _) => {
        if (value.subscriptionType) {
          mappings.set(value.cohortId, value.subscriptionType);
        }
      });
    }
    // Otherwise use cohortMappingsData
    else if (cohortMappingsData?.mappings) {
      cohortMappingsData.mappings.forEach(mapping => {
        if (mapping.subscriptionType) {
          mappings.set(mapping.cohortId, mapping.subscriptionType as CohortSubscriptionType);
        }
      });
    }

    setCohortMappings(mappings);
  }, [cohortMappingsData, defaultCohortValueMappings]);

  // Track if cohort data has changed from the original (for edit mode)
  const hasMetadataChanges = useMemo(() => {
    if (!isEditMode || !cohortToEdit) return false;

    const descriptionChanged = cohortDescription !== (cohortToEdit.description || '');
    const ownersChanged =
      JSON.stringify(cohortOwnerIds.sort()) !==
      JSON.stringify((cohortToEdit.owners?.map((o: any) => o._id) || []).sort());
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(cohortToEdit.filters);
    const excludeFraudHoldingChanged =
      excludeCBsInFraudHolding !== (cohortToEdit.excludeCBsInFraudHolding ?? true);
    const ignoreAutoArchivalChanged =
      ignoreAutoArchival !== (cohortToEdit.ignoreAutoArchivalForLatestVersion ?? false);

    // Check if cohort mappings have changed
    const cohortMappingsChanged = (() => {
      // Convert current cohortMappings to a comparable format
      const currentMappings = Array.from(cohortMappings.entries()).sort((a, b) =>
        a[0].localeCompare(b[0]),
      );
      // Convert original cohortMappingsData to a comparable format
      const originalMappings = cohortMappingsData?.mappings
        ? cohortMappingsData.mappings
            .map(
              mapping =>
                [mapping.cohortId, mapping.subscriptionType] as [string, CohortSubscriptionType],
            )
            .sort((a, b) => a[0].localeCompare(b[0]))
        : [];

      return JSON.stringify(currentMappings) !== JSON.stringify(originalMappings);
    })();

    return (
      descriptionChanged ||
      ownersChanged ||
      filtersChanged ||
      excludeFraudHoldingChanged ||
      ignoreAutoArchivalChanged ||
      cohortMappingsChanged
    );
  }, [
    isEditMode,
    cohortToEdit,
    cohortDescription,
    cohortOwnerIds,
    filters,
    excludeCBsInFraudHolding,
    ignoreAutoArchival,
    cohortMappings,
    cohortMappingsData,
  ]);

  // Determine if filters or fraud holding flag have changed (requires evaluation in edit mode)
  const filtersOrFraudHoldingChanged = useMemo(() => {
    if (!isEditMode || !cohortToEdit) return false;

    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(cohortToEdit.filters);
    const excludeFraudHoldingChanged =
      excludeCBsInFraudHolding !== (cohortToEdit.excludeCBsInFraudHolding ?? true);
    const cohortMappingsChanged = (() => {
      // Convert current cohortMappings to a comparable format
      const currentMappings = Array.from(cohortMappings.entries()).sort((a, b) =>
        a[0].localeCompare(b[0]),
      );
      // Convert original cohortMappingsData to a comparable format
      const originalMappings = cohortMappingsData?.mappings
        ? cohortMappingsData.mappings
            .map(
              mapping =>
                [mapping.cohortId, mapping.subscriptionType] as [string, CohortSubscriptionType],
            )
            .sort((a, b) => a[0].localeCompare(b[0]))
        : [];

      return JSON.stringify(currentMappings) !== JSON.stringify(originalMappings);
    })();

    return filtersChanged || excludeFraudHoldingChanged || cohortMappingsChanged;
  }, [
    isEditMode,
    cohortToEdit,
    filters,
    excludeCBsInFraudHolding,
    cohortMappings,
    cohortMappingsData,
  ]);

  // Determine if form has unsaved changes
  // In edit mode: check if any fields have changed from original
  // In create mode: check if any fields have been populated
  const dirtyFieldsExist = useMemo(() => {
    if (isEditMode) {
      return hasMetadataChanges || filtersOrFraudHoldingChanged;
    }
    return (
      cohortName !== '' ||
      cohortDescription !== '' ||
      !(cohortOwnerIds.includes(currentUserId) && cohortOwnerIds.length === 1) ||
      (isValidFilterGroup(filters) && filters.filtersOrFilterGroups.length > 0) ||
      !excludeCBsInFraudHolding ||
      ignoreAutoArchival !== (workforceProjectId !== undefined)
    );
  }, [
    isEditMode,
    hasMetadataChanges,
    filtersOrFraudHoldingChanged,
    cohortName,
    cohortDescription,
    cohortOwnerIds,
    currentUserId,
    filters,
    excludeCBsInFraudHolding,
    ignoreAutoArchival,
    workforceProjectId,
  ]);

  // Populate form fields from cohortToEdit (for edit mode) or initialCohort (for clone mode)
  useEffect(() => {
    const cohortToPopulate = cohortToEdit || initialCohort;
    if (!cohortToPopulate) {
      return;
    }
    if (cohortToPopulate.name && cohortToPopulate.name !== cohortName) {
      setCohortName(cohortToPopulate?.name);
    }
    if (cohortToPopulate.description && cohortToPopulate.description !== cohortDescription) {
      setCohortDescription(cohortToPopulate?.description);
    }
    if (cohortToPopulate.owners) {
      const initialOwnerIds = cohortToPopulate?.owners?.map((owner: any) => owner._id);
      if (initialOwnerIds && initialOwnerIds.length > 0) {
        setCohortOwnerIds(Array.from(new Set([...initialOwnerIds, currentUserId])));
      }
    }
    if (
      cohortToPopulate.filters &&
      JSON.stringify(cohortToPopulate.filters) !== JSON.stringify(filters)
    ) {
      setFilters(cohortToPopulate?.filters);
    }
    if (cohortToPopulate.excludeCBsInFraudHolding !== undefined) {
      setExcludeCBsInFraudHolding(cohortToPopulate.excludeCBsInFraudHolding);
    }
    if (cohortToPopulate.ignoreAutoArchivalForLatestVersion !== undefined) {
      setIgnoreAutoArchival(cohortToPopulate.ignoreAutoArchivalForLatestVersion);
    }
  }, [cohortToEdit, initialCohort, currentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data: membershipEvalData,
    refetch: refetchSimilarCohorts,
    isFetching: isFetchingSimilarCohorts,
  } = useGetMembershipEval(filters, excludeCBsInFraudHolding, cohortToEdit?._id, {
    enabled: false,
  });

  const exactMatchCohort = membershipEvalData?.exactMatchCohort;

  // Check if the filters are exactly the same as the initial cohort's filters
  const filtersAreClonedFromInitialCohort = useMemo(() => {
    if (!initialCohort) return false;
    return JSON.stringify(initialCohort.filters) === JSON.stringify(filters);
  }, [initialCohort, filters]);

  const resetCohortInfo = () => {
    setCohortName(defaultTitle ?? '');
    setCohortDescription(defaultDescription ?? '');
    setCohortOwnerIds([currentUserId]);
    userHasModifiedFilters.current = false;
    setFilters(effectiveDefaultFilters);
    setExcludeCBsInFraudHolding(true);
    setIgnoreAutoArchival(workforceProjectId !== undefined);

    setCohortCreationCloseConfirmationOpen(false);

    setCohortMappings(new Map());
  };

  // Check if any scoping columns are not paired with other filters in their group
  const hasNonPairedScopedFields = useMemo(() => {
    const checkNonPairedScopedFields = (filterGroup: FilterGroup): boolean => {
      const { filtersOrFilterGroups } = filterGroup;

      // Check if this group has any scoping column filters
      const scopingFilters: any[] = [];
      const nonScopingFilters: any[] = [];
      let hasNestedFilterGroups = false;

      for (const item of filtersOrFilterGroups) {
        if ('filtersOrFilterGroups' in item) {
          // Recursively check nested filter groups
          if (checkNonPairedScopedFields(item as FilterGroup)) {
            return true;
          }
          // A nested filter group counts as paired content for scoping filters
          hasNestedFilterGroups = true;
        } else {
          // This is a Filter
          const filter = item as any;
          if (filter.column?.scopingColumn) {
            scopingFilters.push(filter);
          } else {
            nonScopingFilters.push(filter);
          }
        }
      }

      // If this group has scoping filters but no non-scoping filters AND no nested filter groups, it's invalid
      if (scopingFilters.length > 0 && nonScopingFilters.length === 0 && !hasNestedFilterGroups) {
        return true;
      }

      return false;
    };

    return checkNonPairedScopedFields(filters);
  }, [filters]);

  const filtersAreValid = useMemo(() => {
    return (
      isValidFilterGroup(filters) &&
      filters.filtersOrFilterGroups.length > 0 &&
      !hasNonPairedScopedFields
    );
  }, [filters, hasNonPairedScopedFields]);

  const getDialogContent = () => {
    if (cohortCreationCloseConfirmationOpen) {
      return (
        <CloseConfirmationPanel
          onCancel={() => setCohortCreationCloseConfirmationOpen(false)}
          onConfirm={() => {
            resetCohortInfo();
            onConfirmedClose();
          }}
        />
      );
    }
    return (
      <UpsertCohortPanel
        onCohortCreated={onCohortCreated}
        potentialContributorCount={membershipEvalData?.contributorsMatchingFiltersCount ?? 0}
        isFetchingSimilarCohorts={isFetchingSimilarCohorts}
        exactMatchCohort={
          exactMatchCohort || (filtersAreClonedFromInitialCohort ? initialCohort : undefined)
        }
        refetchSimilarCohorts={refetchSimilarCohorts}
        onExistingCohortClicked={onExistingCohortClicked}
        filters={filters}
        setFilters={handleSetFilters}
        filtersAreValid={filtersAreValid}
        hasNonPairedScopedFields={hasNonPairedScopedFields}
        cohortNameState={[cohortName, setCohortName]}
        cohortDescriptionState={[cohortDescription, setCohortDescription]}
        cohortOwnerIdsState={[cohortOwnerIds, setCohortOwnerIds]}
        excludeCBsInFraudHoldingState={[excludeCBsInFraudHolding, setExcludeCBsInFraudHolding]}
        ignoreAutoArchivalState={[ignoreAutoArchival, setIgnoreAutoArchival]}
        onClose={() => {
          if (dirtyFieldsExist) {
            setCohortCreationCloseConfirmationOpen(true);
          } else {
            resetCohortInfo(); // Reset form state to empty
            onConfirmedClose();
          }
        }}
        clonedFromCohortId={initialCohort?._id}
        resetCohortInfo={resetCohortInfo}
        isEditMode={isEditMode}
        cohortToEdit={cohortToEdit}
        hasMetadataChanges={hasMetadataChanges}
        filtersOrFraudHoldingChanged={filtersOrFraudHoldingChanged}
        inflowCount={membershipEvalData?.inflowCount}
        outflowCount={membershipEvalData?.outflowCount}
        cohortMappings={cohortMappings}
        setCohortMappings={setCohortMappings}
        initialCohort={initialCohort}
        workforceProjectId={workforceProjectId}
        inputColumns={inputColumns}
        scopingColumns={scopingColumns}
      />
    );
  };

  return (
    <Dialog.Content size="4" maxWidth="1000px">
      {getDialogContent()}
    </Dialog.Content>
  );
};
